import api from './api';

// 内联类型定义，避免模块导入问题
export enum ContractType {
  MAIN = 'main',
  AMENDMENT = 'amendment',
  CHANGE = 'change',
}

export enum ContractStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  COMPLETED = 'completed',
  TERMINATED = 'terminated',
}

export enum PaymentMilestoneStatus {
  PENDING = 'pending',
  PARTIAL = 'partial',
  PAID = 'paid',
}

export enum ContractChangeType {
  INCREASE = 'increase',
  DECREASE = 'decrease',
  AMENDMENT = 'amendment',
}

export interface PaymentMilestone {
  _id?: string;
  nodeName: string;
  plannedAmount: number;
  paidAmount: number;
  plannedDate?: string;
  paidDate?: string;
  status: PaymentMilestoneStatus;
  note?: string;
}

export interface ContractChangeRecord {
  _id?: string;
  changeType: ContractChangeType;
  description: string;
  changeAmount: number;
  changeDate: string;
  reason?: string;
}

export interface ContractAttachment {
  _id?: string;
  fileName: string;
  fileUrl: string;
  fileType: string;
  fileSize?: number;
  uploadedAt?: string;
}

export interface ProjectContract {
  _id: string;
  projectId: string | { _id: string; name: string; projectCode?: string };
  projectName?: string;
  contractNo?: string;
  contractName: string;
  contractType: ContractType;
  partyA: string;
  partyB: string;
  totalAmount: number;
  paidAmount: number;
  paymentProgress?: number;
  unpaidAmount?: number;
  signingDate?: string;
  startDate?: string;
  endDate?: string;
  status: ContractStatus;
  paymentMilestones: PaymentMilestone[];
  changeRecords: ContractChangeRecord[];
  attachments: ContractAttachment[];
  description?: string;
  constructionType?: string | null;
  createdBy?: string | { _id: string; username: string; realName?: string };
  createdAt: string;
  updatedAt: string;
}

export interface ProjectContractSummary {
  contractCount: number;
  totalAmount: number;
  totalPaidAmount: number;
  paymentProgress: number;
}

export interface ContractQueryParams {
  projectId?: string;
  status?: ContractStatus;
  contractType?: ContractType;
  keyword?: string;
  constructionType?: string;
  page?: number;
  limit?: number;
}

export interface CreateContractData {
  projectId: string;
  contractNo?: string;
  contractName: string;
  contractType?: ContractType;
  partyA: string;
  partyB: string;
  totalAmount: number;
  paidAmount?: number;
  signingDate?: string;
  startDate?: string;
  endDate?: string;
  status?: ContractStatus;
  paymentMilestones?: PaymentMilestone[];
  changeRecords?: ContractChangeRecord[];
  attachments?: ContractAttachment[];
  description?: string;
  constructionType: string;
}

export interface UpdateContractData extends Partial<CreateContractData> {}

// API响应类型
interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

// 分页响应类型
interface PaginatedResponse<T = any> extends ApiResponse<T[]> {
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export class ProjectContractService {
  static async getContracts(
    params: ContractQueryParams = {}
  ): Promise<PaginatedResponse<ProjectContract>> {
    return api.get('/project-contracts', { params }) as Promise<PaginatedResponse<ProjectContract>>;
  }

  static async getContractById(id: string): Promise<ApiResponse<ProjectContract>> {
    return api.get(`/project-contracts/${id}`) as Promise<ApiResponse<ProjectContract>>;
  }

  static async createContract(
    data: CreateContractData
  ): Promise<ApiResponse<ProjectContract>> {
    return api.post('/project-contracts', data) as Promise<ApiResponse<ProjectContract>>;
  }

  static async updateContract(
    id: string,
    data: UpdateContractData
  ): Promise<ApiResponse<ProjectContract>> {
    return api.put(`/project-contracts/${id}`, data) as Promise<ApiResponse<ProjectContract>>;
  }

  static async deleteContract(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/project-contracts/${id}`) as Promise<ApiResponse<void>>;
  }

  static async getProjectSummary(
    projectId: string
  ): Promise<ApiResponse<ProjectContractSummary>> {
    return api.get(`/project-contracts/project/${projectId}/summary`) as Promise<ApiResponse<ProjectContractSummary>>;
  }

  static async addMilestone(
    contractId: string,
    data: { nodeName: string; plannedAmount: number; plannedDate?: string; note?: string }
  ): Promise<ApiResponse<ProjectContract>> {
    return api.post(`/project-contracts/${contractId}/milestones`, data) as Promise<ApiResponse<ProjectContract>>;
  }

  static async deleteMilestone(
    contractId: string,
    milestoneId: string
  ): Promise<ApiResponse<ProjectContract>> {
    return api.delete(`/project-contracts/${contractId}/milestones/${milestoneId}`) as Promise<ApiResponse<ProjectContract>>;
  }

  static async addChangeRecord(
    contractId: string,
    data: { changeType: ContractChangeType; description: string; changeAmount: number; changeDate?: string; reason?: string }
  ): Promise<ApiResponse<ProjectContract>> {
    return api.post(`/project-contracts/${contractId}/change-records`, data) as Promise<ApiResponse<ProjectContract>>;
  }

  static async addAttachment(
    contractId: string,
    data: { fileName: string; fileUrl: string; fileType?: string; fileSize?: number }
  ): Promise<ApiResponse<ProjectContract>> {
    return api.post(`/project-contracts/${contractId}/attachments`, data) as Promise<ApiResponse<ProjectContract>>;
  }

  static async deleteAttachment(
    contractId: string,
    attachmentId: string
  ): Promise<ApiResponse<ProjectContract>> {
    return api.delete(`/project-contracts/${contractId}/attachments/${attachmentId}`) as Promise<ApiResponse<ProjectContract>>;
  }

  static getStatusOptions(): Array<{ value: ContractStatus; label: string }> {
    return [
      { value: ContractStatus.DRAFT, label: '草稿' },
      { value: ContractStatus.ACTIVE, label: '已生效' },
      { value: ContractStatus.COMPLETED, label: '已完成' },
      { value: ContractStatus.TERMINATED, label: '已终止' },
    ];
  }

  static getStatusLabel(status: ContractStatus): string {
    return this.getStatusOptions().find((item) => item.value === status)?.label || status;
  }

  static getStatusColor(status: ContractStatus): string {
    switch (status) {
      case ContractStatus.DRAFT: return 'default';
      case ContractStatus.ACTIVE: return 'green';
      case ContractStatus.COMPLETED: return 'blue';
      case ContractStatus.TERMINATED: return 'red';
      default: return 'default';
    }
  }

  static getTypeOptions(): Array<{ value: ContractType; label: string }> {
    return [
      { value: ContractType.MAIN, label: '主合同' },
      { value: ContractType.AMENDMENT, label: '补充协议' },
      { value: ContractType.CHANGE, label: '变更' },
    ];
  }

  static getTypeLabel(type: ContractType): string {
    return this.getTypeOptions().find((item) => item.value === type)?.label || type;
  }

  static getTypeColor(type: ContractType): string {
    switch (type) {
      case ContractType.MAIN: return 'blue';
      case ContractType.AMENDMENT: return 'orange';
      case ContractType.CHANGE: return 'purple';
      default: return 'default';
    }
  }

  static getMilestoneStatusOptions(): Array<{ value: PaymentMilestoneStatus; label: string }> {
    return [
      { value: PaymentMilestoneStatus.PENDING, label: '待支付' },
      { value: PaymentMilestoneStatus.PARTIAL, label: '部分支付' },
      { value: PaymentMilestoneStatus.PAID, label: '已支付' },
    ];
  }

  static getMilestoneStatusLabel(status: PaymentMilestoneStatus): string {
    return this.getMilestoneStatusOptions().find((item) => item.value === status)?.label || status;
  }

  static getMilestoneStatusColor(status: PaymentMilestoneStatus): string {
    switch (status) {
      case PaymentMilestoneStatus.PENDING: return 'default';
      case PaymentMilestoneStatus.PARTIAL: return 'orange';
      case PaymentMilestoneStatus.PAID: return 'green';
      default: return 'default';
    }
  }

  static getChangeTypeOptions(): Array<{ value: ContractChangeType; label: string }> {
    return [
      { value: ContractChangeType.INCREASE, label: '增加金额' },
      { value: ContractChangeType.DECREASE, label: '减少金额' },
      { value: ContractChangeType.AMENDMENT, label: '条款变更' },
    ];
  }

  static getChangeTypeLabel(type: ContractChangeType): string {
    return this.getChangeTypeOptions().find((item) => item.value === type)?.label || type;
  }

  static getChangeTypeColor(type: ContractChangeType): string {
    switch (type) {
      case ContractChangeType.INCREASE: return 'green';
      case ContractChangeType.DECREASE: return 'red';
      case ContractChangeType.AMENDMENT: return 'orange';
      default: return 'default';
    }
  }
}
